import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { SHOW_MESSAGE } from '../../../../../actions/actionTypes';
import { useDeleteOfficeReview, useGetOfficeReviews } from '../../../../../domain/OfficeReviews';
import useClampedPage from '../../../../../hooks/useClampedPage';

/**
 * The office Reviews tab's data, one page at a time.
 *
 * Composes the two domain use cases, owns the page index, and derives the gates
 * the screen needs so the screen itself stays declarative. Mirrors
 * `Campaigns/hooks/useProfessionalCampaigns.js`.
 *
 * The success message still goes through Redux: `SHOW_MESSAGE` drives the app's
 * one toast surface, which is UI state and stays in Redux by design. Only the
 * server state moved to React Query.
 *
 * @param {Object} params
 * @param {string} [params.officeId] - Absent while the profile is still loading
 */
export const useOfficeReviews = ({ officeId }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);

  const {
    reviews,
    total,
    pageSize,
    isError,
    isInitialLoading,
    isFetchingPage,
    refetch,
  } = useGetOfficeReviews({ officeId, page });

  // Deleting the last review on the last page strands the admin past the end,
  // where the endpoint returns nothing. Pull them back rather than dead-ending
  // on an empty table. A nullish total means "unknown" and never clamps.
  useClampedPage(page, total, pageSize, setPage);

  const { deleteReview } = useDeleteOfficeReview({ officeId });

  return {
    reviews,
    // The stepper needs a number; null only ever means "not known yet", and the
    // stepper hides itself at 0 anyway.
    total: total ?? 0,
    page,
    pageSize,
    goToPage: setPage,

    isError,

    // No office id means the profile is still resolving the user, so the query
    // is disabled and has neither data nor an error. Treated as loading,
    // because the empty state would otherwise claim the office has no reviews.
    isPending: isInitialLoading || !officeId,

    isFetchingPage,
    refetch,

    /**
     * Deletes a review and announces it. The list refetches itself — the
     * mutation invalidates every page of this office's query — so nothing here
     * touches the rows.
     */
    removeReview: (reviewId) =>
      deleteReview(reviewId, {
        onSuccess: () =>
          dispatch({ type: SHOW_MESSAGE, payload: { message: 'Review has been deleted.' } }),
      }),
  };
};

export default useOfficeReviews;
